<template>
  <div class="range-field-container" :class="{ 'dark-mode': darkMode }">
    <div class="label-container">
      <p>
        {{ label }}
      </p>

      <p class="second-label">
        {{ secondLabel }}
      </p>
    </div>

    <div class="range-field-content" :class="{ disabled: isDisabled }">
      <input
        ref="range"
        type="range"
        :min="min"
        :max="max"
        :step="step"
        v-model="innerValue"
        :disabled="isDisabled"
        :style="{ backgroundSize: backgroundSize }"
        @input="updateSlider"
      />

      <input
        type="number"
        :placeholder="min"
        v-model="inputValue"
        autocomplete="off"
        :disabled="isDisabled"
        @click="clearInput"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'SliderField',

  props: {
    label: {
      type: String,
      default: '',
    },

    secondLabel: {
      type: String,
      default: '',
    },

    min: {
      type: Number,
      default: 1,
    },

    max: {
      type: Number,
      default: 10,
    },

    initialValue: {
      type: [Number, String],
      default: 0,
    },

    step: {
      type: Number,
      default: 1,
    },

    isDisabled: {
      type: Boolean,
      default: false,
    },

    darkMode: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      innerValue: 0,
      inputValue: 0,
      backgroundSize: '0% 100%',
    };
  },

  mounted() {
    if (this.initialValue) {
      this.innerValue = this.initialValue;
      this.inputValue = this.initialValue;
    } else {
      this.innerValue = this.min;
      this.inputValue = this.min;
    }

    this.updateBackground();
  },

  watch: {
    innerValue(value) {
      if (!value) {
        this.inputValue = this.min;
      } else {
        this.inputValue = value;
      }

      this.$emit('input', value ? value : this.min);
    },

    inputValue(value) {
      if (!value) {
        this.innerValue = this.min;
      } else {
        this.innerValue = value;

        if (value > this.max) {
          this.innerValue = this.max;
          this.inputValue = this.max;
        }

        if (value < this.min) {
          this.innerValue = this.min;
        }
      }

      this.updateBackground();
    },
  },

  methods: {
    updateSlider() {
      this.updateBackground();
    },

    updateBackground() {
      this.backgroundSize =
        ((this.innerValue - this.min) * 100) / (this.max - this.min) + '% 100%';
    },

    syncValue(value) {
      if (!value) {
        this.innerValue = this.min;
        this.inputValue = this.min;
      } else {
        this.innerValue = value;
        this.inputValue = value;
      }
    },

    clearInput() {
      this.inputValue = '';
    },
  },
};
</script>

<style lang="scss" scoped>
.range-field-container {
  transition: all 0.3s ease;

  .label-container {
    display: flex;
    transition: color 0.3s ease;

    p {
      color: #6d6f6f;
      transition: color 0.3s ease;

      &.second-label {
        color: #6d6f6f;
      }

      &:last-child {
        margin-left: auto;
      }
    }
  }

  .range-field-content {
    width: 100%;
    display: flex;
    align-items: center;
    transition: all 0.3s ease;

    &.disabled {
      opacity: 0.8;

      input {
        &[type='range'] {
          &::-webkit-slider-thumb {
            background: #dadada;
          }
        }
      }
    }

    input {
      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      &[type='number'] {
        text-align: center;
        margin-left: 0.5rem;
        height: 35px;
        width: 35px;
        background: #fff;
        border-radius: 5px;
        display: flex;
        justify-content: center;
        align-items: center;
        border: 1px solid #dadada;
        font-size: 14px;
        color: #333;
        transition: all 0.3s ease;
      }

      &[type='range'] {
        width: 100%;
        height: 12px;
        border-radius: 5px;
        border: 1px solid #dadada;
        margin-left: 3px;
        margin-right: 3px;
        background-image: linear-gradient(to right, #ff9c00 0%, #ff9c00 100%);
        background-repeat: no-repeat;
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        transition: all 0.3s ease;

        &:focus {
          outline: none;
        }

        &:focus::-webkit-slider-runnable-track {
          background: transparent;
        }

        &::-webkit-slider-thumb {
          -webkit-appearance: none;
          height: 25px;
          width: 25px;
          border-radius: 50%;
          background: #ff9c00;
          border: 3px solid #fff;
          cursor: ew-resize;
          transition: all 0.3s ease;
        }

        &::-webkit-slider-runnable-track {
          -webkit-appearance: none;
          box-shadow: none;
          border: none;
          background: transparent;
        }

        &:focus::-ms-fill-lower {
          background: transparent;
        }

        &:focus::-ms-fill-upper {
          background: transparent;
        }

        &::-moz-range-track {
          box-shadow: none;
          border: none;
          background: transparent;
        }

        &::-moz-range-thumb {
          height: 18px;
          width: 18px;
          border-radius: 50%;
          background: #ff9c00;
          border: 3px solid #fff;
          cursor: ew-resize;
        }
      }
    }
  }

  &.dark-mode {
    .label-container {
      p {
        color: #f0f0f0;

        &.second-label {
          color: #a0aab8;
        }
      }
    }

    .range-field-content {
      input {
        &[type='number'] {
          background: #394653;
          border-color: #3a4856;
          color: #f0f0f0;
        }

        &[type='range'] {
          border-color: #3a4856;
          background-image: linear-gradient(to right, #ff8b00 0%, #ff8b00 100%);

          &::-webkit-slider-thumb {
            background: #ff8b00;
            border-color: #394653;
          }

          &::-moz-range-thumb {
            background: #ff8b00;
            border-color: #394653;
          }
        }
      }

      &.disabled {
        input {
          &[type='number'] {
            background: #2b3746;
            border-color: #3a4856;
            color: #8a9aaa;
          }

          &[type='range'] {
            &::-webkit-slider-thumb {
              background: #3a4856;
            }

            &::-moz-range-thumb {
              background: #3a4856;
            }
          }
        }
      }
    }
  }
}
</style>
